import { defu } from 'defu'
import { state } from '../state'
import type { LocationData } from '../types'
import { convertLocationToSnakeCase, convertLocationToCamelCase } from '../utils/case-converter'
import type { Faro } from '@grafana/faro-web-sdk'
import { store } from '../store'

/**
 * Set or get the user location.
 * @param location - The location to set in camelCase format.
 * @returns The merged location in camelCase format.
 * @example
 * ```ts
 * const location = await resolveLocation()
 * console.log(location)
 *
 * await resolveLocation({
 *   city: 'São Paulo',
 *   country: 'Brazil',
 * })
 * ```
 */
export async function resolveLocation(customLocation?: Partial<LocationData>): Promise<LocationData> {
  state.locationPromise ??= store.loadLocation()

  // Read path: only await the location fetch. Do NOT block on `state.faro` —
  // it resolves only once Faro is initialized (after `load()`), and returns a
  // never-resolving promise until then (see state.ts `get faro`). Consumers
  // that call `resolveLocation()` before load completes (e.g. `autoLoad: false`
  // with a deferred load) would otherwise hang forever. Faro is only needed on
  // the write path below.
  const locationFromRequest = await state.locationPromise.catch((reason) => {
    console.error('[resolveLocation] Erro ao resolver localização:', reason)
    return null
  })

  // Convert location data (snake_case) to camelCase for internal use
  const locationInCamelCase = locationFromRequest ? convertLocationToCamelCase(locationFromRequest) as LocationData : undefined
  const mergedLocation = defu(locationInCamelCase, customLocation)

  if (customLocation) {
    // Write path: persist the override into Faro session meta + storage.
    // Faro is genuinely required here, so awaiting it is correct.
    const faro = await state.faro as Faro
    const snakeCaseLocation = convertLocationToSnakeCase(mergedLocation)

    faro.api.setSession(defu({
      attributes: {
        ...snakeCaseLocation,
      },
    }, faro.metas.value.session))

    // Store in snake_case format
    await store.setLocation(snakeCaseLocation)
  }

  return mergedLocation
}
